import { fullJitter, POLICIES, uuidv7 } from '@one-deployment/shared'
import type { ApiError as ApiErrorBody } from '@one-deployment/api'

/** Every non-2xx answer becomes one of these; screens render `what` and `todo`. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly what: string | null = null,
    readonly todo: string | null = null,
    /** For 409 `conflict`: the row as it is now, so a screen can show what changed. */
    readonly current: unknown = undefined,
  ) {
    super(message)
    this.name = 'ApiError'
  }

  /** Somebody else changed it first (409 conflict, 412): refetch and let the person look again. */
  get stale(): boolean {
    return this.status === 412 || (this.status === 409 && this.code === 'conflict')
  }
}

type Options = {
  method?: string
  body?: unknown
  signal?: AbortSignal
  /** The version the screen read; sent as `If-Match: "v<n>"` (05 §1.3). */
  version?: number
  /** One key per user intent. Defaults to a fresh one per call, reused across its retries. */
  key?: string
}

/** Answers that mean "the request may not have landed, ask again with the same key". */
const retriable = (status: number, code: string) => status === 0 || status >= 500 || (status === 409 && code === 'in_progress')

/**
 * Typed fetch for the Worker. Every mutation carries an `Idempotency-Key` that
 * is minted once and reused on every retry of that intent, so a retry after a
 * dropped response replays the stored answer instead of acting twice (05 §1).
 * Reads are not retried here; Query owns that.
 */
export async function api<T>(path: string, opts: Options = {}): Promise<T> {
  const method = opts.method ?? (opts.body === undefined ? 'GET' : 'POST')
  const headers: Record<string, string> = { accept: 'application/json' }
  if (opts.body !== undefined) headers['content-type'] = 'application/json'
  const mutation = method !== 'GET'
  if (mutation) headers['idempotency-key'] = opts.key ?? uuidv7()
  if (opts.version !== undefined) headers['if-match'] = `"v${opts.version}"`

  const attempts = mutation ? POLICIES.call.maxAttempts : 1
  for (let attempt = 1; ; attempt++) {
    try {
      return await once<T>(path, method, headers, opts)
    } catch (err) {
      if (!(err instanceof ApiError) || attempt >= attempts || !retriable(err.status, err.code)) throw err
      await new Promise((r) => setTimeout(r, Math.max(100, fullJitter(attempt, POLICIES.call))))
    }
  }
}

async function once<T>(path: string, method: string, headers: Record<string, string>, opts: Options): Promise<T> {
  let res: Response
  try {
    res = await fetch(path, {
      method,
      headers,
      credentials: 'same-origin',
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
      signal: opts.signal,
    })
  } catch (err) {
    if ((err as Error).name === 'AbortError') throw err
    throw new ApiError(0, 'network', 'Network error', "Can't reach the dashboard API.", 'Check your connection; this retries automatically.')
  }

  if (res.status === 204) return undefined as T
  const text = await res.text()
  const data = text ? safeJson(text) : null
  if (!res.ok) throw toError(res, data)
  return data as T
}

function toError(res: Response, data: unknown): ApiError {
  const body = (data as Partial<ApiErrorBody> | null)?.error
  const code = body?.code ?? `http_${res.status}`
  const message = body?.message ?? res.statusText
  // Only a stale version carries `current`; other 409s ("no container running")
  // say what is wrong in their message and must not read as an edit clash.
  if (res.status === 412 || (res.status === 409 && code === 'conflict' && body?.current !== undefined)) {
    return new ApiError(res.status, code, message, 'Somebody changed this while you had it open.', 'The latest version has been loaded — check it and try again.', body?.current)
  }
  if (res.status === 428) {
    // The Worker refuses a mutation without a key or a version: a client bug, not the person's.
    return new ApiError(res.status, code, message, 'This page is out of date with the server.', 'Reload the page and try again.')
  }
  return new ApiError(res.status, code, message, message || null)
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return { error: { code: 'internal', message: text.slice(0, 200) } }
  }
}

export const get = <T>(path: string, signal?: AbortSignal) => api<T>(path, { signal })
export const post = <T>(path: string, body: unknown = {}) => api<T>(path, { method: 'POST', body })
export const put = <T>(path: string, body: unknown = {}) => api<T>(path, { method: 'PUT', body })
/** PATCH always names the version it was editing: a blind overwrite is refused with 428. */
export const patch = <T>(path: string, body: unknown, version: number) => api<T>(path, { method: 'PATCH', body, version })
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' })

export const enc = encodeURIComponent
